/**
 * Invite people to watch the household playlists.
 *
 * Only the administrator can open this screen. An invite is an email that may
 * request a magic link; revoking it signs them out on the next request.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { invites as invitesApi, type Invite } from '../lib/api';
import { ErrorNotice, Loading, Spinner } from '../components/Feedback';
import { Icon } from '../components/Icon';
import { formatRelative } from '../lib/format';
import { useSession } from '../lib/session';
import { navigate } from '../router/router';

export function People() {
  const { user } = useSession();
  const [rows, setRows] = useState<Invite[] | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const result = await invitesApi.list();
    setRows(result.invites);
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/accounts');
      return;
    }
    void reload().catch((caught: unknown) => setError(caught));
  }, [reload, user?.role]);

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await invitesApi.create(email.trim());
      setEmail('');
      await reload();
      if (result.delivered === 'response' && result.link) {
        const code = result.code ? ` or this code: ${result.code}` : '';
        setNotice(`Invited ${result.invite.email}. No mail provider, so share this link: ${result.link}${code}`);
      } else {
        setNotice(`A sign-in link and code are on its way to ${result.invite.email}.`);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await invitesApi.revoke(id);
      await reload();
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  };

  if (rows === null && !error) {
    return (
      <div className="page">
        <Loading label="Loading people" />
      </div>
    );
  }

  return (
    <div className="page">
      <div className="wrap stack">
        <div className="page__head">
          <div className="page__title">
            <h1>People</h1>
            <span className="muted">Invited addresses can watch the playlists. Only you can change them.</span>
          </div>
        </div>

        <ErrorNotice error={error} />
        {notice ? <p className="muted">{notice}</p> : null}

        <form className="card stack" onSubmit={(event) => void invite(event)}>
          <div className="field">
            <label htmlFor="invite-email">Invite by email</label>
            <input
              id="invite-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@example.com"
              required
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={busy || !email.trim()}>
            {busy ? <Spinner /> : <Icon name="plus" size={16} />}
            Send invite
          </button>
        </form>

        {rows?.length ? (
          <ul className="stack" style={{ listStyle: 'none', padding: 0, margin: 0, gap: 'var(--s-2)' }}>
            {rows.map((row) => (
              <li key={row.id} className="card row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="stack stack--tight">
                  <strong>{row.email}</strong>
                  <span className="muted">
                    {row.acceptedAt ? `Joined ${formatRelative(row.acceptedAt)}` : `Invited ${formatRelative(row.createdAt)}`}
                  </span>
                </div>
                <button type="button" className="btn btn--sm btn--danger" onClick={() => void revoke(row.id)} disabled={busy}>
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No one invited yet.</p>
        )}
      </div>
    </div>
  );
}
